import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiX, FiUser, FiChevronRight, FiCheckCircle, FiTarget } = FiIcons;

const UserProfileForm = ({ isOpen, onClose, onSave, initialProfile = null, allowSkip = false }) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    goal: initialProfile?.goal || '',
    activityLevel: initialProfile?.activityLevel || '',
    sex: initialProfile?.sex || '',
    age: initialProfile?.age || '',
    height: initialProfile?.height || { value: '', unit: 'cm' },
    weight: initialProfile?.weight || { value: '', unit: 'kg' },
    diet: initialProfile?.diet || '',
    mealPreferences: initialProfile?.mealPreferences || [],
    calculatedTargets: initialProfile?.calculatedTargets || {
      calories: '',
      protein: '',
      carbs: '',
      fat: '',
    },
  });

  const [formErrors, setFormErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [isAutoAdvancing, setIsAutoAdvancing] = useState(false);
  
  // Track if user has manually interacted with the form to prevent auto-advancing on load
  const userInteracted = useRef(false);

  // Height unit conversion helpers
  const convertHeightToCm = (feet, inches) => {
    return (feet * 30.48) + (inches * 2.54);
  };

  const convertHeightToFeetInches = (cm) => {
    const totalInches = cm / 2.54;
    const feet = Math.floor(totalInches / 12);
    const inches = Math.round(totalInches % 12);
    return { feet, inches };
  };

  // Weight unit conversion
  const convertWeightToKg = (lbs) => {
    return lbs * 0.45359237;
  };

  const convertWeightToLbs = (kg) => {
    return kg / 0.45359237;
  };

  // Handle height unit change
  const handleHeightUnitChange = (newUnit) => {
    userInteracted.current = true;
    if (formData.height.value && formData.height.unit !== newUnit) {
      if (newUnit === 'cm' && formData.height.unit === 'ft') {
        const cm = convertHeightToCm(formData.height.feet || 0, formData.height.inches || 0);
        setFormData({ ...formData, height: { value: Math.round(cm), unit: 'cm' } });
      } else if (newUnit === 'ft' && formData.height.unit === 'cm') {
        const { feet, inches } = convertHeightToFeetInches(formData.height.value);
        setFormData({ ...formData, height: { feet, inches, unit: 'ft' } });
      }
    } else {
      setFormData({ ...formData, height: { ...formData.height, unit: newUnit } });
    }
  };

  // Handle weight unit change
  const handleWeightUnitChange = (newUnit) => {
    userInteracted.current = true;
    if (formData.weight.value && formData.weight.unit !== newUnit) {
      if (newUnit === 'kg' && formData.weight.unit === 'lb') {
        const kg = convertWeightToKg(formData.weight.value);
        setFormData({ ...formData, weight: { value: Math.round(kg * 10) / 10, unit: 'kg' } });
      } else if (newUnit === 'lb' && formData.weight.unit === 'kg') {
        const lbs = convertWeightToLbs(formData.weight.value);
        setFormData({ ...formData, weight: { value: Math.round(lbs), unit: 'lb' } });
      }
    } else {
      setFormData({ ...formData, weight: { ...formData.weight, unit: newUnit } });
    }
  };

  // Validate current step
  const validateStep = () => {
    const errors = {};
    switch (step) {
      case 1: // Goal
        if (!formData.goal) errors.goal = 'Please select your goal';
        break;
      case 2: // Activity Level
        if (!formData.activityLevel) errors.activityLevel = 'Please select your activity level';
        break;
      case 3: // Sex
        if (!formData.sex) errors.sex = 'Please select your sex';
        break;
      case 4: // Age
        if (!formData.age) {
          errors.age = 'Please enter your age';
        } else if (formData.age < 15 || formData.age > 100) {
          errors.age = 'Please enter a valid age between 15-100';
        }
        break;
      case 5: // Height
        if (formData.height.unit === 'cm') {
          if (!formData.height.value) {
            errors.height = 'Please enter your height';
          } else if (formData.height.value < 130 || formData.height.value > 230) {
            errors.height = 'Please enter a valid height between 130-230 cm';
          }
        } else {
          if (!formData.height.feet) {
            errors.height = 'Please enter your height (feet)';
          } else if (formData.height.feet < 4 || formData.height.feet > 7) {
            errors.height = 'Please enter a valid height between 4-7 feet';
          }
          if (formData.height.inches === undefined || formData.height.inches === '') {
            formData.height.inches = 0; // Auto-set inches to 0 if not provided
          } else if (formData.height.inches < 0 || formData.height.inches > 11) {
            errors.height = 'Inches must be between 0-11';
          }
        }
        break;
      case 6: // Weight
        if (!formData.weight.value) {
          errors.weight = 'Please enter your weight';
        } else if (
          (formData.weight.unit === 'kg' && (formData.weight.value < 40 || formData.weight.value > 200)) ||
          (formData.weight.unit === 'lb' && (formData.weight.value < 88 || formData.weight.value > 440))
        ) {
          errors.weight = `Please enter a valid weight between ${formData.weight.unit === 'kg' ? '40-200 kg' : '88-440 lbs'}`;
        }
        break;
      case 7: // Diet
        if (!formData.diet) errors.diet = 'Please select your nutritional preference';
        break;
      case 8: // Meal Preferences
        if (!formData.mealPreferences || formData.mealPreferences.length === 0) {
          errors.mealPreferences = 'Please select at least one meal style';
        }
        break;
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle next step
  const nextStep = () => {
    if (validateStep()) {
      if (step === 8) {
        calculateTargets();
      } else {
        setStep(step + 1);
        setIsAutoAdvancing(false); // Reset auto-advance flag on manual next
        userInteracted.current = false; // Reset interaction flag
      }
    }
  };

  // Handle option selection with auto-advance
  const handleOptionSelect = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    userInteracted.current = true;
    setIsAutoAdvancing(true);
  };

  // Auto-advance on selection - ONLY if triggered by user interaction
  useEffect(() => {
    if (isAutoAdvancing && userInteracted.current) {
      if (step === 1 && formData.goal) {
        const timer = setTimeout(nextStep, 300);
        return () => clearTimeout(timer);
      }
      if (step === 2 && formData.activityLevel) {
        const timer = setTimeout(nextStep, 300);
        return () => clearTimeout(timer);
      }
      if (step === 3 && formData.sex) {
        const timer = setTimeout(nextStep, 300);
        return () => clearTimeout(timer);
      }
      if (step === 7 && formData.diet) {
        const timer = setTimeout(nextStep, 300);
        return () => clearTimeout(timer);
      }
      // Note: Step 8 (Meal Preferences) is multi-select, so no auto-advance
    }
  }, [formData, step, isAutoAdvancing]);

  // Handle previous step
  const prevStep = () => {
    if (step > 1) {
      setStep(step - 1);
      setIsAutoAdvancing(false);
      userInteracted.current = false;
    }
  };

  // Calculate BMR and macro targets
  const calculateTargets = () => {
    setIsCalculating(true);
    setTimeout(() => {
      try {
        // Convert height to cm for calculation
        let heightCm;
        if (formData.height.unit === 'cm') {
          heightCm = parseFloat(formData.height.value);
        } else {
          heightCm = convertHeightToCm(
            parseFloat(formData.height.feet),
            parseFloat(formData.height.inches || 0)
          );
        }

        // Convert weight to kg for calculation
        let weightKg;
        if (formData.weight.unit === 'kg') {
          weightKg = parseFloat(formData.weight.value);
        } else {
          weightKg = convertWeightToKg(parseFloat(formData.weight.value));
        }

        // Calculate BMR using Mifflin-St Jeor Equation
        let bmr;
        if (formData.sex === 'male') {
          bmr = 10 * weightKg + 6.25 * heightCm - 5 * parseInt(formData.age) + 5;
        } else {
          bmr = 10 * weightKg + 6.25 * heightCm - 5 * parseInt(formData.age) - 161;
        }

        // Apply activity level multiplier
        let activityMultiplier;
        switch (formData.activityLevel) {
          case 'sedentary': activityMultiplier = 1.2; break;
          case 'lightly-active': activityMultiplier = 1.375; break;
          case 'moderately-active': activityMultiplier = 1.55; break;
          case 'active': activityMultiplier = 1.725; break;
          case 'very-active': activityMultiplier = 1.9; break;
          default: activityMultiplier = 1.2;
        }

        // Apply goal adjustment
        let goalAdjustment;
        switch (formData.goal) {
          case 'lose-weight': goalAdjustment = -500; break;
          case 'build-muscle': goalAdjustment = 500; break;
          case 'maintain': goalAdjustment = 0; break;
          default: goalAdjustment = 0;
        }

        // Calculate total daily calories
        const totalCalories = Math.round((bmr * activityMultiplier) + goalAdjustment);

        // Calculate macros based on diet preference
        let proteinPct, fatPct, carbsPct;
        switch (formData.diet) {
          case 'balanced': proteinPct = 0.30; fatPct = 0.35; carbsPct = 0.35; break;
          case 'high-protein': proteinPct = 0.45; fatPct = 0.25; carbsPct = 0.30; break;
          case 'low-carb': proteinPct = 0.40; fatPct = 0.40; carbsPct = 0.20; break;
          case 'high-carb': proteinPct = 0.30; fatPct = 0.20; carbsPct = 0.50; break;
          case 'keto': proteinPct = 0.25; fatPct = 0.70; carbsPct = 0.05; break;
          default: proteinPct = 0.30; fatPct = 0.35; carbsPct = 0.35;
        }

        // Calculate grams of each macro
        const proteinGrams = Math.round((totalCalories * proteinPct) / 4);
        const fatGrams = Math.round((totalCalories * fatPct) / 9);
        const carbGrams = Math.round((totalCalories * carbsPct) / 4);

        // Update formData with calculated targets
        const updatedFormData = {
          ...formData,
          calculatedTargets: {
            calories: totalCalories,
            protein: proteinGrams,
            carbs: carbGrams,
            fat: fatGrams,
          },
        };

        setFormData(updatedFormData);
        setIsCalculating(false);
        setStep(9); // Move to results step (Now step 9)
      } catch (error) {
        console.error('Error calculating targets:', error);
        setIsCalculating(false);
        setFormErrors({ calculation: 'Error calculating targets. Please check your inputs.' });
      }
    }, 1000); // Simulate calculation time
  };

  // Save profile and close
  const handleSave = () => {
    onSave(formData);
  };

  const handleMealPreferenceToggle = (prefId) => {
    userInteracted.current = true;
    setFormData(prev => {
      const currentPrefs = prev.mealPreferences || [];
      if (currentPrefs.includes(prefId)) {
        return { ...prev, mealPreferences: currentPrefs.filter(p => p !== prefId) };
      } else {
        return { ...prev, mealPreferences: [...currentPrefs, prefId] };
      }
    });
  };

  if (!isOpen) return null;

  // Content for each step
  const renderStepContent = () => {
    switch (step) {
      case 1: // Goal
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-gray-800">What's your goal?</h3>
            <div className="grid grid-cols-1 gap-4">
              {[
                { id: 'lose-weight', label: 'Lose Weight', description: 'Calorie deficit to reduce body fat' },
                { id: 'maintain', label: 'Maintain Weight', description: 'Balanced calories for current weight' },
                { id: 'build-muscle', label: 'Build Muscle', description: 'Calorie surplus to support muscle growth' },
              ].map(goal => (
                <button
                  key={goal.id}
                  onClick={() => handleOptionSelect('goal', goal.id)}
                  className={`p-4 border-2 rounded-xl text-left transition-colors ${
                    formData.goal === goal.id
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200 hover:border-indigo-300'
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-medium text-lg">{goal.label}</div>
                      <div className="text-sm text-gray-500">{goal.description}</div>
                    </div>
                    {formData.goal === goal.id && (
                      <div className="text-indigo-600">
                        <SafeIcon icon={FiCheckCircle} className="text-xl" />
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
            {formErrors.goal && <p className="text-red-500 text-sm">{formErrors.goal}</p>}
          </div>
        );
      case 2: // Activity Level
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-gray-800">What's your activity level?</h3>
            <p className="text-gray-600 text-sm">This helps us calculate your daily calorie needs.</p>
            <div className="grid grid-cols-1 gap-4">
              {[
                { id: 'sedentary', label: 'Sedentary', description: 'Little or no exercise, desk job' },
                { id: 'lightly-active', label: 'Lightly Active', description: 'Light exercise 1-2 times/week' },
                { id: 'moderately-active', label: 'Moderately Active', description: 'Moderate exercise 2-3 times/week' },
                { id: 'active', label: 'Active', description: 'Hard exercise 4-5 times/week' },
                { id: 'very-active', label: 'Very Active', description: 'Physical job or hard exercise 6-7 times/week' },
              ].map(activity => (
                <button
                  key={activity.id}
                  onClick={() => handleOptionSelect('activityLevel', activity.id)}
                  className={`p-4 border-2 rounded-xl text-left transition-colors ${
                    formData.activityLevel === activity.id
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200 hover:border-indigo-300'
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-medium text-lg">{activity.label}</div>
                      <div className="text-sm text-gray-500">{activity.description}</div>
                    </div>
                    {formData.activityLevel === activity.id && (
                      <div className="text-indigo-600">
                        <SafeIcon icon={FiCheckCircle} className="text-xl" />
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
            {formErrors.activityLevel && <p className="text-red-500 text-sm">{formErrors.activityLevel}</p>}
          </div>
        );
      case 3: // Sex
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-gray-800">What's your biological sex?</h3>
            <p className="text-gray-600 text-sm">We use this for metabolic rate calculation.</p>
            <div className="grid grid-cols-2 gap-4">
              {[
                { id: 'male', label: 'Male' },
                { id: 'female', label: 'Female' },
              ].map(sex => (
                <button
                  key={sex.id}
                  onClick={() => handleOptionSelect('sex', sex.id)}
                  className={`p-6 border-2 rounded-xl transition-colors ${
                    formData.sex === sex.id
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200 hover:border-indigo-300'
                  }`}
                >
                  <div className="flex flex-col items-center justify-center">
                    <div className="font-medium text-lg">{sex.label}</div>
                    {formData.sex === sex.id && (
                      <div className="text-indigo-600 mt-2">
                        <SafeIcon icon={FiCheckCircle} className="text-xl" />
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
            {formErrors.sex && <p className="text-red-500 text-sm">{formErrors.sex}</p>}
          </div>
        );
      case 4: // Age
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-gray-800">What's your age?</h3>
            <div className="max-w-xs">
              <div className="relative">
                <input
                  type="number"
                  value={formData.age}
                  onChange={(e) => {
                    userInteracted.current = true;
                    setFormData({ ...formData, age: e.target.value });
                  }}
                  min="15"
                  max="100"
                  className="w-full pl-4 pr-20 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter your age"
                />
                <span className="absolute right-4 top-3 text-gray-500">years</span>
              </div>
              {formErrors.age && <p className="text-red-500 text-sm mt-2">{formErrors.age}</p>}
            </div>
          </div>
        );
      case 5: // Height
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-gray-800">What's your height?</h3>
            <div className="flex gap-4 mb-4">
              <button
                onClick={() => handleHeightUnitChange('cm')}
                className={`px-4 py-2 rounded-lg ${
                  formData.height.unit === 'cm'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Centimeters
              </button>
              <button
                onClick={() => handleHeightUnitChange('ft')}
                className={`px-4 py-2 rounded-lg ${
                  formData.height.unit === 'ft'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Feet & Inches
              </button>
            </div>
            {formData.height.unit === 'cm' ? (
              <div className="max-w-xs">
                <div className="relative">
                  <input
                    type="number"
                    value={formData.height.value}
                    onChange={(e) => {
                      userInteracted.current = true;
                      setFormData({
                        ...formData,
                        height: { ...formData.height, value: e.target.value },
                      });
                    }}
                    className="w-full pl-4 pr-16 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Height in cm"
                  />
                  <span className="absolute right-4 top-3 text-gray-500">cm</span>
                </div>
              </div>
            ) : (
              <div className="flex gap-4 max-w-xs">
                <div className="flex-1 relative">
                  <input
                    type="number"
                    value={formData.height.feet}
                    onChange={(e) => {
                      userInteracted.current = true;
                      setFormData({
                        ...formData,
                        height: { ...formData.height, feet: e.target.value },
                      });
                    }}
                    className="w-full pl-4 pr-12 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Feet"
                    min="4"
                    max="7"
                  />
                  <span className="absolute right-4 top-3 text-gray-500">ft</span>
                </div>
                <div className="flex-1 relative">
                  <input
                    type="number"
                    value={formData.height.inches}
                    onChange={(e) => {
                      userInteracted.current = true;
                      setFormData({
                        ...formData,
                        height: { ...formData.height, inches: e.target.value },
                      });
                    }}
                    className="w-full pl-4 pr-12 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Inches"
                    min="0"
                    max="11"
                  />
                  <span className="absolute right-4 top-3 text-gray-500">in</span>
                </div>
              </div>
            )}
            {formErrors.height && <p className="text-red-500 text-sm">{formErrors.height}</p>}
          </div>
        );
      case 6: // Weight
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-gray-800">What's your weight?</h3>
            <div className="flex gap-4 mb-4">
              <button
                onClick={() => handleWeightUnitChange('kg')}
                className={`px-4 py-2 rounded-lg ${
                  formData.weight.unit === 'kg'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Kilograms
              </button>
              <button
                onClick={() => handleWeightUnitChange('lb')}
                className={`px-4 py-2 rounded-lg ${
                  formData.weight.unit === 'lb'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Pounds
              </button>
            </div>
            <div className="max-w-xs">
              <div className="relative">
                <input
                  type="number"
                  value={formData.weight.value}
                  onChange={(e) => {
                    userInteracted.current = true;
                    setFormData({
                      ...formData,
                      weight: { ...formData.weight, value: e.target.value },
                    });
                  }}
                  className="w-full pl-4 pr-16 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder={`Weight in ${formData.weight.unit === 'kg' ? 'kilograms' : 'pounds'}`}
                  step="0.1"
                />
                <span className="absolute right-4 top-3 text-gray-500">{formData.weight.unit}</span>
              </div>
              {formErrors.weight && <p className="text-red-500 text-sm mt-2">{formErrors.weight}</p>}
            </div>
          </div>
        );
      case 7: // Diet
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-gray-800">What's your nutritional preference?</h3>
            <p className="text-gray-600 text-sm">This helps us calculate your macro targets.</p>
            <div className="grid grid-cols-1 gap-4">
              {[
                { id: 'balanced', label: 'Balanced', description: '30% Protein / 35% Fat / 35% Carbs' },
                { id: 'high-protein', label: 'High Protein', description: '45% Protein / 25% Fat / 30% Carbs' },
                { id: 'low-carb', label: 'Low Carb', description: '40% Protein / 40% Fat / 20% Carbs' },
                { id: 'high-carb', label: 'High Carb', description: '30% Protein / 20% Fat / 50% Carbs' },
                { id: 'keto', label: 'Keto', description: '25% Protein / 70% Fat / 5% Carbs' },
              ].map(diet => (
                <button
                  key={diet.id}
                  onClick={() => handleOptionSelect('diet', diet.id)}
                  className={`p-4 border-2 rounded-xl text-left transition-colors ${
                    formData.diet === diet.id
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200 hover:border-indigo-300'
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-medium text-lg">{diet.label}</div>
                      <div className="text-sm text-gray-500">{diet.description}</div>
                    </div>
                    {formData.diet === diet.id && (
                      <div className="text-indigo-600">
                        <SafeIcon icon={FiCheckCircle} className="text-xl" />
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
            {formErrors.diet && <p className="text-red-500 text-sm">{formErrors.diet}</p>}
          </div>
        );
      case 8: // Meal Preferences (NEW STEP)
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-gray-800">What kind of meals do you enjoy most?</h3>
            <p className="text-gray-600 text-sm">Select all that apply. We'll use this to generate suggested meals for you.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { id: 'comfort', label: 'Comfort Classics', description: 'Chicken, pasta, potatoes, burgers' },
                { id: 'bowls', label: 'High-Protein Bowls', description: 'Bowls with chicken, beef, rice, veggies, or eggs' },
                { id: 'mexican', label: 'Mexican-Style', description: 'Tacos, burrito bowls, rice, beans, salsa' },
                { id: 'italian', label: 'Italian-Style', description: 'Pasta dishes, cheesy meals, garlic flavors' },
                { id: 'asian', label: 'Asian-Style', description: 'Stir-fries, noodle bowls, rice bowls' },
                { id: 'fresh', label: 'Fresh & Light', description: 'Salads, wraps, grilled proteins, fresh veggies' },
              ].map(pref => (
                <button
                  key={pref.id}
                  onClick={() => handleMealPreferenceToggle(pref.id)}
                  className={`p-4 border-2 rounded-xl text-left transition-colors h-full ${
                    formData.mealPreferences?.includes(pref.id)
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200 hover:border-indigo-300'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-medium text-lg mb-1">{pref.label}</div>
                      <div className="text-sm text-gray-500">{pref.description}</div>
                    </div>
                    {formData.mealPreferences?.includes(pref.id) && (
                      <div className="text-indigo-600 mt-1">
                        <SafeIcon icon={FiCheckCircle} className="text-xl" />
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
            {formErrors.mealPreferences && <p className="text-red-500 text-sm">{formErrors.mealPreferences}</p>}
          </div>
        );
      case 9: // Results (Previously Step 8)
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-2">
              <SafeIcon icon={FiTarget} className="text-2xl text-indigo-600" />
              <h3 className="text-xl font-bold text-gray-800">Your Calculated Targets</h3>
            </div>
            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="text-sm font-medium text-indigo-700 mb-1">Daily Calories</div>
                  <div className="text-3xl font-bold text-indigo-800">
                    {formData.calculatedTargets.calories} <span className="text-lg font-normal">kcal</span>
                  </div>
                </div>
                <div>
                  <div className="text-sm font-medium text-indigo-700 mb-1">Macronutrient Split</div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <div className="text-xl font-bold text-red-600">
                        {formData.calculatedTargets.protein}g
                      </div>
                      <div className="text-xs text-indigo-700">Protein</div>
                    </div>
                    <div>
                      <div className="text-xl font-bold text-yellow-600">
                        {formData.calculatedTargets.carbs}g
                      </div>
                      <div className="text-xs text-indigo-700">Carbs</div>
                    </div>
                    <div>
                      <div className="text-xl font-bold text-green-600">
                        {formData.calculatedTargets.fat}g
                      </div>
                      <div className="text-xs text-indigo-700">Fat</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-indigo-200">
                <div className="text-sm text-indigo-700">
                  These targets are based on your profile and preferences. You can adjust them manually in the calculator if needed.
                </div>
              </div>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <div className="flex items-start gap-2">
                <SafeIcon icon={FiCheckCircle} className="text-green-600 mt-1" />
                <div>
                  <div className="font-medium text-green-800">Your profile is ready!</div>
                  <div className="text-sm text-green-700">
                    Click "Save Profile" to continue to the meal calculator with your personalized targets and meal suggestions.
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return <div>Unknown step</div>;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={allowSkip ? onClose : undefined}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <SafeIcon icon={FiUser} className="text-indigo-600" />
            {initialProfile ? 'Update Your Profile' : 'Create Your Profile'}
          </h2>
          {allowSkip && (
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
            >
              <SafeIcon icon={FiX} className="text-xl" />
            </button>
          )}
        </div>

        <div className="p-6">
          {/* Progress Steps */}
          <div className="flex justify-center mb-8">
            <div className="flex items-center space-x-2 max-w-full overflow-x-auto">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((s) => (
                <div key={s} className="flex items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                      step >= s ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {s}
                  </div>
                  {s < 9 && <div className={`w-4 h-1 ${step > s ? 'bg-indigo-600' : 'bg-gray-200'}`} />}
                </div>
              ))}
            </div>
          </div>

          {/* Step Content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {isCalculating ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-600 border-t-transparent mb-4"></div>
                  <p className="text-lg text-gray-700">Calculating your personalized targets...</p>
                </div>
              ) : (
                renderStepContent()
              )}
            </motion.div>
          </AnimatePresence>

          {/* General Error Message */}
          {formErrors.calculation && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
              {formErrors.calculation}
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="p-6 border-t border-gray-200 flex justify-between">
          {step > 1 ? (
            <button
              onClick={prevStep}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Back
            </button>
          ) : (
            <div></div> // Empty div for spacing
          )}

          {step < 9 ? (
            <button
              onClick={nextStep}
              className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors flex items-center gap-2"
            >
              <span>Next</span>
              <SafeIcon icon={FiChevronRight} />
            </button>
          ) : (
            <button
              onClick={handleSave}
              className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
            >
              Save Profile
            </button>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default UserProfileForm;